import React, { Fragment } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import {
    CollectionIcon,
    CreditCardIcon,
    ClipboardListIcon,
    PlayIcon,
    UsersIcon,
    UserCircleIcon,
    XIcon,
} from '@heroicons/react/outline'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid'
import RenderOnRole from './RenderOnRole'
import { classNames } from './ui'

const primary = [
    { name: 'Transacciones', href: '/transacciones', icon: ClipboardListIcon, match: ['/', '/transacciones'] },
]

const admin = [
    { name: 'Cuentas CLABE', href: '/cuentas', icon: CreditCardIcon },
    { name: 'Canales de cobro', href: '/canales', icon: CollectionIcon },
    { name: 'Usuarios', href: '/usuarios', icon: UsersIcon },
]

const developers = [
    { name: 'Webhooks', href: '/webhooks', icon: PlayIcon },
]

const account = [
    { name: 'Mi perfil', href: '/perfil', icon: UserCircleIcon },
]

const Brand = ({ collapsed, onToggle }) => (
    <div className={classNames('relative px-4 pb-2 pt-6', collapsed && 'flex flex-col items-center px-2')}>
        <div className="inline-flex flex-col items-center">
            <img
                className={classNames('brightness-0 invert', collapsed ? 'h-10 w-auto' : 'h-16 w-auto')}
                src="/gplogo.png"
                alt="GotPay"
            />
            {!collapsed && <p className="mt-1 text-lg italic tracking-tight text-white/90">CoDi</p>}
        </div>
        {onToggle && (
            <button
                type="button"
                onClick={onToggle}
                className={classNames(
                    'hidden rounded-lg p-1 text-slate-400 hover:bg-white/10 hover:text-white lg:inline-flex',
                    collapsed ? 'mx-auto mt-3' : 'absolute right-2 top-4'
                )}
                title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            >
                {collapsed ? <ChevronRightIcon className="h-5 w-5" /> : <ChevronLeftIcon className="h-5 w-5" />}
            </button>
        )}
    </div>
)

function NavItems({ items, onNavigate, collapsed }) {
    const location = useLocation()
    return items.map((item) => {
        const active = item.match ? item.match.includes(location.pathname) : location.pathname === item.href
        return (
            <NavLink
                key={item.name}
                to={item.href}
                title={item.name}
                onClick={onNavigate}
                className={classNames(
                    active ? 'bg-white/10 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white',
                    'group flex items-center rounded-xl py-2 text-sm font-medium',
                    collapsed ? 'justify-center px-2' : 'px-3'
                )}
            >
                <item.icon className={classNames('h-5 w-5 shrink-0 opacity-80', !collapsed && 'mr-3')} aria-hidden="true" />
                {!collapsed && item.name}
            </NavLink>
        )
    })
}

function NavSections({ collapsed, onNavigate }) {
    return (
        <nav className="mt-6 flex-1 space-y-8 overflow-y-auto px-2">
            <div className="space-y-1">
                {!collapsed && (
                    <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Operación</p>
                )}
                <NavItems items={primary} onNavigate={onNavigate} collapsed={collapsed} />
            </div>
            <RenderOnRole roles={['admin']}>
                <div className="space-y-1">
                    {!collapsed && (
                        <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Administración</p>
                    )}
                    <NavItems items={admin} onNavigate={onNavigate} collapsed={collapsed} />
                </div>
            </RenderOnRole>
            <div className="space-y-1">
                {!collapsed && (
                    <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Desarrolladores</p>
                )}
                <NavItems items={developers} onNavigate={onNavigate} collapsed={collapsed} />
            </div>
            <div className="space-y-1">
                {!collapsed && (
                    <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Cuenta</p>
                )}
                <NavItems items={account} onNavigate={onNavigate} collapsed={collapsed} />
            </div>
        </nav>
    )
}

const Sidebar = ({ open, onClose, collapsed, onToggle }) => {
    return (
        <>
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-40 lg:hidden" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-slate-900/60" />
                    </Transition.Child>
                    <div className="fixed inset-0 flex z-40">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-200 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-200 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative flex w-72 flex-col bg-brand-900 pb-4 pt-5">
                                <div className="absolute right-0 top-0 -mr-12 pt-2">
                                    <button
                                        type="button"
                                        className="ml-1 flex h-10 w-10 items-center justify-center rounded-full text-white"
                                        onClick={onClose}
                                    >
                                        <XIcon className="h-6 w-6" />
                                    </button>
                                </div>
                                <Brand />
                                <NavSections onNavigate={onClose} />
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>

            <div
                className={classNames(
                    'hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-[width] duration-200',
                    collapsed ? 'lg:w-20' : 'lg:w-56'
                )}
            >
                <div className="flex min-h-0 flex-1 flex-col bg-brand-900">
                    <Brand collapsed={collapsed} onToggle={onToggle} />
                    <NavSections collapsed={collapsed} />
                    {!collapsed && (
                        <div className="border-t border-white/10 px-5 py-4 text-xs text-slate-400">
                            Panel CoDi · GotPay
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default Sidebar
